<template>
	<div class="tb-goods-page">
		<template v-if="pageView === 'list'">
			<section class="filter-card">
				<el-form :model="form" inline size="small" class="filter-form" label-width="0" @submit.native.prevent="onSearch">
					<div class="filter-row">
						<el-form-item>
							<el-select
								v-model="form.retail_shop_id"
								placeholder="全部"
								filterable
								clearable
								:loading="shopLoading"
								class="filter-control"
								@change="onShopChange"
							>
								<el-option label="全部" value="" />
								<el-option
									v-for="item in shopList"
									:key="item.id"
									:label="item.shop_name || item.mall_name || ('店铺' + item.id)"
									:value="String(item.id)"
								/>
							</el-select>
						</el-form-item>
						<el-form-item>
							<el-select v-model="form.sync_rule" placeholder="同步规则" class="filter-control">
								<el-option label="同步规则-全部" value="" />
								<el-option v-for="item in syncRuleOptions" :key="item.value" :label="item.label" :value="item.value" />
							</el-select>
						</el-form-item>
						<el-form-item>
							<el-select v-model="form.stock_sync" placeholder="同步库存" class="filter-control">
								<el-option label="同步库存-全部" value="" />
								<el-option label="同步库存-同步" :value="1" />
								<el-option label="同步库存-不同步" :value="0" />
							</el-select>
						</el-form-item>
						<el-form-item>
							<el-select v-model="form.goods_status" placeholder="商品状态" class="filter-control" @change="onGoodsStatusChange">
								<el-option
									v-for="item in goodsStatusOptions"
									:key="String(item.value)"
									:label="item.label"
									:value="item.value"
								/>
							</el-select>
						</el-form-item>
						<el-form-item>
							<el-select v-model="form.is_deleted" placeholder="删除状态" class="filter-control" @change="onDeletedChange">
								<el-option label="删除状态-全部" :value="-1" />
								<el-option label="删除状态-未删除" :value="0" />
								<el-option label="删除状态-已删除" :value="1" />
							</el-select>
						</el-form-item>
					</div>
					<div class="filter-row filter-row--second">
						<el-form-item class="keyword-item">
							<span class="keyword-label">关键字</span>
							<el-input v-model="form.keyword" placeholder="商品标题" clearable class="keyword-input" @keyup.enter.native="onSearch" />
						</el-form-item>
						<el-form-item class="keyword-item">
							<span class="keyword-label">库存同步状态</span>
							<el-select v-model="form.stock_sync_status" placeholder="全部" class="labeled-select">
								<el-option label="全部" value="" />
								<el-option label="未同步" :value="0" />
								<el-option label="成功" :value="2" />
								<el-option label="失败" :value="3" />
								<el-option label="部分成功" :value="4" />
							</el-select>
						</el-form-item>
						<el-form-item class="keyword-item">
							<span class="keyword-label">价格同步状态</span>
							<el-select v-model="form.price_sync_status" placeholder="全部" class="labeled-select">
								<el-option label="全部" value="" />
								<el-option label="未同步" :value="0" />
								<el-option label="成功" :value="2" />
								<el-option label="失败" :value="3" />
							</el-select>
						</el-form-item>
						<div class="filter-actions">
							<el-button type="primary" class="btn-search" @click="onSearch">搜索</el-button>
							<el-button class="btn-ghost" @click="onReset">重置</el-button>
						</div>
					</div>
				</el-form>
			</section>

			<section class="toolbar-card">
				<div class="toolbar-group">
					<div class="toolbar-btns">
						<button
							v-for="item in bulkPrimary"
							:key="item.key"
							type="button"
							class="tool-btn tool-btn--brand"
							:disabled="saleStatusSubmitting"
							@click="onBulkAction(item)"
						>{{ item.label }}</button>
					</div>
				</div>
				<div v-if="selectedRows.length" class="selection-tip">
					已选 <strong>{{ selectedRows.length }}</strong> 条
				</div>
			</section>

			<el-alert
				v-if="listError"
				:title="listError"
				type="warning"
				show-icon
				:closable="false"
			/>

			<section class="table-card" ref="tableCard">
				<el-table
					v-loading="loading || saleStatusSubmitting"
					:data="list"
					:height="tableHeight"
					border
					size="small"
					class="goods-table"
					@selection-change="handleSelectionChange"
				>
					<el-table-column type="selection" width="42" align="center" fixed="left" />
					<el-table-column type="index" label="序号" width="56" align="center" :index="indexMethod" />
					<el-table-column label="图片" width="76" align="center">
						<template slot-scope="scope">
							<div class="thumb">
								<el-image
									v-if="rowImage(scope.row)"
									:src="rowImage(scope.row)"
									:preview-src-list="[rowImage(scope.row)]"
									fit="cover"
								/>
								<i v-else class="el-icon-picture-outline"></i>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="店铺名称" min-width="120" align="center">
						<template slot-scope="scope">{{ shopNameOf(scope.row) }}</template>
					</el-table-column>
					<el-table-column label="商品编号/款号" min-width="140" align="center">
						<template slot-scope="scope">
							<div class="cell-main">{{ displayCell(scope.row.goods_id || scope.row.num_iid) }}</div>
							<div v-if="scope.row.outer_id" class="cell-sub">{{ scope.row.outer_id }}</div>
						</template>
					</el-table-column>
					<el-table-column label="商品状态" width="80" align="center">
						<template slot-scope="scope">
							<span class="status-tag" :class="tbGoodsStatusClass(scope.row)">
								{{ formatTbGoodsStatus(scope.row) }}
							</span>
						</template>
					</el-table-column>
					<el-table-column label="同步规则" min-width="168" align="center">
						<template slot-scope="scope">
							<el-select v-model="scope.row.sync_rule" size="small" placeholder="请选择" class="rule-select" @change="onSyncRuleChange(scope.row)">
								<el-option v-for="item in syncRuleTableOptions" :key="item.value" :label="item.label" :value="item.value" />
							</el-select>
							<button v-if="showRuleConfigBtn(scope.row)" type="button" class="link-btn" @click="onRuleConfig(scope.row)">参数配置</button>
						</template>
					</el-table-column>
					<el-table-column label="同步库存" width="110" align="center">
						<template slot-scope="scope">
							<div class="status-cell" :class="Number(scope.row.stock_sync) === 1 ? 'is-on' : 'is-off'">
								<el-switch
									:value="getStockSyncValue(scope.row)"
									:active-value="1"
									:inactive-value="0"
									active-color="#ff4714"
									inactive-color="#dcdfe6"
									:disabled="isTbGoodDeleted(scope.row)"
									@change="onStockSyncChange(scope.row, $event)"
								/>
								<span class="status-label">{{ getStockSyncValue(scope.row) === 1 ? '同步' : '不同步' }}</span>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="库存同步时间" min-width="138" align="center">
						<template slot-scope="scope">
							<div class="sync-status-cell">
								<span
									v-if="formatGoodsSyncStatus(scope.row.stock_sync_status || scope.row.stock_state) !== '-'"
									class="sync-status-tag"
									:class="'sync-status-tag--' + syncStatusTagType(scope.row.stock_sync_status || scope.row.stock_state)"
								>{{ formatGoodsSyncStatus(scope.row.stock_sync_status || scope.row.stock_state) }}</span>
								<span v-else class="sync-status-empty">-</span>
							</div>
							<div class="cell-time">{{ formatTime(scope.row.stock_sync_time) }}</div>
						</template>
					</el-table-column>
					<el-table-column label="价格同步时间" min-width="138" align="center">
						<template slot-scope="scope">
							<div class="sync-status-cell">
								<span
									v-if="formatGoodsSyncStatus(scope.row.price_sync_status || scope.row.price_state) !== '-'"
									class="sync-status-tag"
									:class="'sync-status-tag--' + syncStatusTagType(scope.row.price_sync_status || scope.row.price_state)"
								>{{ formatGoodsSyncStatus(scope.row.price_sync_status || scope.row.price_state) }}</span>
								<span v-else class="sync-status-empty">-</span>
							</div>
							<div class="cell-time">{{ formatTime(scope.row.price_sync_time) }}</div>
						</template>
					</el-table-column>
					<el-table-column label="商品名称" min-width="160" align="center">
						<template slot-scope="scope">{{ displayCell(scope.row.goods_name || scope.row.title) }}</template>
					</el-table-column>
					<el-table-column label="数据更新时间" min-width="138" align="center">
						<template slot-scope="scope">{{ formatTime(scope.row.modified || scope.row.data_update_time) }}</template>
					</el-table-column>
					<el-table-column label="操作" width="240" align="center" fixed="right">
						<template slot-scope="scope">
							<div class="row-actions">
								<button type="button" class="action-chip" @click="onViewSku(scope.row)">SKU编码</button>
								<button type="button" class="action-chip" @click="onSkuLog(scope.row)">SKU更新记录</button>
								<button type="button" class="action-chip" @click="onChannelConfig(scope.row)">渠道配置</button>
								<button v-if="isTbGoodDeleted(scope.row)" type="button" class="action-chip" @click="onRestoreRow(scope.row)">恢复</button>
								<button v-else type="button" class="action-chip" @click="onDeleteRow(scope.row)">删除</button>
							</div>
						</template>
					</el-table-column>
				</el-table>
			</section>

			<section class="pager-card">
				<el-pagination
					background
					layout="total, sizes, prev, pager, next, jumper"
					:page-sizes="[20, 50]"
					:current-page.sync="page"
					:page-size.sync="pageSize"
					:total="total"
					@current-change="handleCurrentChange"
					@size-change="handleSizeChange"
				/>
			</section>
		</template>

		<section v-else-if="pageView === 'skuLog'" class="rule-config-panel">
			<div class="rule-config-head">
				<el-button type="primary" size="small" icon="el-icon-arrow-left" class="back-list-btn" @click="closeSkuLog">返回商品列表</el-button>
				<span class="rule-config-goods">{{ skuLogRow && (skuLogRow.goods_name || skuLogRow.title || skuLogRow.goods_id || skuLogRow.num_iid) || '' }}</span>
			</div>
			<div class="rule-config-body">
				<taobao-goods-sku-log v-if="skuLogRow" :product-id="skuLogRow.id" />
			</div>
		</section>

		<section v-else-if="pageView === 'channelConfig'" class="rule-config-panel">
			<div class="rule-config-head">
				<el-button type="primary" size="small" icon="el-icon-arrow-left" class="back-list-btn" @click="closeChannelConfig">返回商品列表</el-button>
				<span class="rule-config-goods">{{ channelRow && (channelRow.goods_name || channelRow.title || channelRow.goods_id || channelRow.num_iid) || '' }}</span>
			</div>
			<div class="rule-config-body">
				<taobao-goods-channel-shell
					v-if="channelRow"
					:key="'ch-' + channelRow.id"
					:product-id="channelRow.id"
					:initial-sync-rule="channelRow.sync_rule"
					:retail-shop-id="resolveShopId(channelRow)"
					@close="closeChannelConfig"
					@rule-change="onChannelRuleChange"
				/>
			</div>
		</section>

		<el-dialog
			:title="skuTitle"
			:visible.sync="skuVisible"
			width="1280px"
			:close-on-click-modal="false"
			@closed="onSkuClosed"
		>
			<el-table v-loading="skuLoading" :data="skuList" size="small">
				<el-table-column label="SKU编号" min-width="140" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ stringifySkuId(scope.row.sku_id) || '-' }}</template>
				</el-table-column>
				<el-table-column label="SKU商家编码" min-width="220" align="center">
					<template slot-scope="scope">
						<div class="sku-outer-id-cell">
							<el-input
								:value="scope.row.outer_id"
								size="mini"
								placeholder="商家编码"
								:disabled="skuSaving"
								@input="onSkuOuterIdInput(scope.row, $event)"
							/>
							<p v-if="!canEditSkuOuterId(scope.row)" class="sku-outer-id-hint sku-outer-id-hint--warn">缺少本地SKU主键，请刷新后重试</p>
							<template v-else>
								<p v-if="skuColorWarningText(scope.row)" class="sku-outer-id-hint sku-outer-id-hint--warn">{{ skuColorWarningText(scope.row) }}</p>
								<p v-if="skuGeneratePreviewText(scope.row)" class="sku-outer-id-hint">{{ skuGeneratePreviewText(scope.row) }}</p>
							</template>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="颜色分类（淘宝）" min-width="150" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ displayCell(scope.row.color) }}</template>
				</el-table-column>
				<el-table-column label="尺码（淘宝）" min-width="120" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ displayCell(scope.row.size) }}</template>
				</el-table-column>
				<el-table-column label="解析尺码" width="150" align="center">
					<template slot-scope="scope">
						<div v-if="hasParsedSizeKey(scope.row.size_key)" class="size-key-cell">
							<div class="cell-main">{{ scope.row.size_key }}</div>
						</div>
						<span v-else class="size-key-empty">未解析出尺码，请到SKU编码编辑</span>
					</template>
				</el-table-column>
				<el-table-column label="款号" min-width="120" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ displayCell(scope.row.style_number) }}</template>
				</el-table-column>
				<el-table-column label="规格" min-width="180" align="center" show-overflow-tooltip>
					<template slot-scope="scope">{{ displayCell(scope.row.properties_name) }}</template>
				</el-table-column>
				<el-table-column label="价格" width="90" align="center">
					<template slot-scope="scope">{{ displayCell(scope.row.price) }}</template>
				</el-table-column>
				<el-table-column label="库存" width="80" align="center">
					<template slot-scope="scope">{{ displayCell(scope.row.quantity) }}</template>
				</el-table-column>
			</el-table>
			<span slot="footer">
				<el-button size="small" :disabled="skuBusy" @click="skuVisible = false">取消</el-button>
				<el-button
					size="small"
					:loading="skuGeneratingAll"
					:disabled="skuLoading || skuSaving || !skuList.length"
					@click="onGenerateAllSkuOuterIds"
				>一键生成全部</el-button>
				<el-button
					type="primary"
					size="small"
					:loading="skuSaving"
					:disabled="skuLoading || skuBusy || !skuList.length"
					@click="onSaveAllSkuOuterIds"
				>保存</el-button>
			</span>
		</el-dialog>

		<el-dialog
			:title="configTitle"
			:visible.sync="configVisible"
			width="480px"
			:close-on-click-modal="false"
		>
			<p class="config-tip">当前规则为未设置。保存将写入淘宝商品推送配置；手工指定请用「渠道配置」绑定渠道。</p>
			<el-form size="small" label-width="90px">
				<el-form-item label="规则类型">
					<el-input :value="configType" disabled />
				</el-form-item>
			</el-form>
			<span slot="footer">
				<el-button size="small" @click="configVisible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="configSaving" @click="onSavePushConfig">保存配置</el-button>
			</span>
		</el-dialog>

		<el-dialog :title="saleFailTitle" :visible.sync="saleFailVisible" width="560px">
			<div class="sale-fail-summary">{{ saleFailSummary }}</div>
			<el-table :data="saleFailLogs" border size="small" max-height="320">
				<el-table-column prop="num_iid" label="商品编号" min-width="160" />
				<el-table-column prop="err_msg" label="失败原因" min-width="240" />
			</el-table>
			<span slot="footer">
				<el-button type="primary" size="small" @click="saleFailVisible = false">知道了</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
	import { getApiErrorMessage } from '@/constants/retailPlatform'
	import TaobaoGoodsSkuLog from './goodsSkuLog.vue'
	import TaobaoGoodsChannelShell from './goodsChannelShell.vue'
	import {
		TB_SHOP_API,
		TB_GOOD_API,
		TB_GOOD_SYNC_TIMEOUT,
		TB_GOOD_SALE_STATUS_TIMEOUT,
		getRequestErrorMessage,
		isRequestTimeout,
		formatTbGoodSyncStats,
		stringifySkuId,
		isTbGoodDeleted,
		formatTbGoodsStatus,
		tbGoodsStatusClass,
		formatGoodsSyncStatus,
		syncStatusTagType,
		resolvePushConfigType,
		isTbShopPushStrategy,
		isTbProductPushStrategy,
		resolveTbShopRulePath,
		formatSkuOuterIdRuleLabel,
		hasParsedSizeKey,
	} from '@/constants/taobaoShop'

	export default {
		name: 'TaobaoGoods',
		components: {
			TaobaoGoodsSkuLog,
			TaobaoGoodsChannelShell,
		},
		data() {
			return {
				pageView: 'list',
				shopList: [],
				shopLoading: false,
				list: [],
				loading: false,
				saleStatusSubmitting: false,
				saleFailVisible: false,
				saleFailTitle: '批量操作结果',
				saleFailSummary: '',
				saleFailLogs: [],
				listError: '',
				page: 1,
				pageSize: 20,
				total: 0,
				tableHeight: 480,
				selectedRows: [],
				form: this.emptyForm(),
				syncRuleOptions: [
					{ value: 'manual', label: '同步规则-手工指定' },
					{ value: 'fixed_price_channel_shop', label: '同步规则-一口价选渠道-通用规则' },
					{ value: 'fixed_price_channel_product', label: '同步规则-一口价选渠道-单独规则' },
					{ value: 'safety_stock_price_shop', label: '同步规则-通用安全库存规则改价格' },
					{ value: 'safety_stock_price_product', label: '同步规则-单独安全库存规则改价格' },
					{ value: 'none', label: '同步规则-未设置' },
				],
				syncRuleTableOptions: [
					{ value: 'manual', label: '手工指定' },
					{ value: 'fixed_price_channel_shop', label: '一口价选渠道-通用规则' },
					{ value: 'fixed_price_channel_product', label: '一口价选渠道-单独规则' },
					{ value: 'safety_stock_price_shop', label: '通用安全库存规则改价格' },
					{ value: 'safety_stock_price_product', label: '单独安全库存规则改价格' },
					{ value: 'none', label: '未设置' },
				],
				goodsStatusOptions: [
					{ label: '商品状态-全部', value: '' },
					{ label: '商品状态-上架', value: 1 },
					{ label: '商品状态-下架', value: 2 },
					{ label: '商品状态-售罄', value: 3 },
				],
				bulkPrimary: [
					{ key: 'fixed_price_channel_shop', label: '一口价·通用规则' },
					{ key: 'safety_stock_price_shop', label: '安全库存改价·通用' },
					{ key: 'download', label: '下载商品' },
					{ key: 'update_info', label: '批量更新信息' },
					{ key: 'batch_onsale', label: '批量上架' },
					{ key: 'batch_offsale', label: '批量下架' },
					{ key: 'restore', label: '删除恢复' },
				],
				skuVisible: false,
				skuLoading: false,
				skuSaving: false,
				skuGeneratingAll: false,
				skuList: [],
				skuTitle: 'SKU编码',
				skuLogRow: null,
				channelRow: null,
				configVisible: false,
				configSaving: false,
				configRow: null,
				configType: 'manual',
				configTitle: '参数配置',
			}
		},
		computed: {
			skuBusy() {
				if (this.skuSaving || this.skuGeneratingAll) {
					return true
				}
				return (this.skuList || []).some((row) => row && row._generating)
			},
		},
		created() {
			this.applyQueryShop()
			this.fetchShops()
			this.fetchList()
		},
		mounted() {
			this.calcTableHeight()
			window.addEventListener('resize', this.calcTableHeight)
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.calcTableHeight)
		},
		watch: {
			'$route.query.retail_shop_id'(val) {
				const next = val == null || val === '' ? '' : String(val)
				if (next === String(this.form.retail_shop_id || '')) {
					return
				}
				this.form.retail_shop_id = next
				this.page = 1
				this.fetchList()
			},
		},
		methods: {
			stringifySkuId,
			isTbGoodDeleted,
			formatTbGoodsStatus,
			tbGoodsStatusClass,
			formatGoodsSyncStatus,
			syncStatusTagType,
			hasParsedSizeKey,
			emptyForm() {
				return {
					retail_shop_id: '',
					keyword: '',
					sync_rule: '',
					stock_sync: '',
					goods_status: 1,
					is_deleted: 0,
					stock_sync_status: '',
					price_sync_status: '',
				}
			},
			applyQueryShop() {
				const shopId = this.$route.query && this.$route.query.retail_shop_id
				this.form.retail_shop_id = shopId == null || shopId === '' ? '' : String(shopId)
			},
			displayCell(value) {
				if (value == null || value === '') {
					return '-'
				}
				return value
			},
			rowImage(row) {
				return (row && (row.image_url || row.pic_url)) || ''
			},
			shopNameOf(row) {
				if (row && (row.shop_name || row.mall_name)) {
					return row.shop_name || row.mall_name
				}
				const shopId = row && row.retail_shop_id
				const shop = this.shopList.find((item) => String(item.id) === String(shopId))
				return (shop && (shop.shop_name || shop.mall_name)) || '-'
			},
			formatTime(value) {
				if (value == null || value === '' || Number(value) === 0) {
					return '-'
				}
				if (typeof value === 'string' && value.indexOf('-') !== -1) {
					return value
				}
				return this.$moment.unix(Number(value)).format('YYYY-MM-DD HH:mm:ss')
			},
			indexMethod(index) {
				return (this.page - 1) * this.pageSize + index + 1
			},
			calcTableHeight() {
				this.$nextTick(() => {
					const card = this.$refs.tableCard
					if (!card) {
						return
					}
					const h = card.getBoundingClientRect().height
					this.tableHeight = h > 360 ? Math.floor(h) : 360
				})
			},
			normalizeRow(row) {
				if (!row) {
					return row
				}
				const next = Object.assign({}, row)
				next.pic_url = next.image_url || next.pic_url || ''
				next.image_url = next.pic_url
				next.goods_name = next.goods_name || next.title || ''
				next.title = next.goods_name
				next.goods_id = next.goods_id || next.num_iid || ''
				next.num_iid = next.goods_id
				next.sync_rule = next.sync_rule || next.push_strategy || 'none'
				next.sku_id = stringifySkuId(next.sku_id)
				return next
			},
			fetchShops() {
				this.shopLoading = true
				this.$axios.get(TB_SHOP_API.shopList).then((res) => {
					if (res.data.code == 0) {
						this.shopList = (res.data.data && res.data.data.list) || []
					} else {
						this.shopList = []
					}
				}).catch((err) => {
					console.log(err)
					this.shopList = []
				}).finally(() => {
					this.shopLoading = false
				})
			},
			buildListParams() {
				const params = {
					page: this.page,
					page_size: this.pageSize,
				}
				if (this.form.retail_shop_id) {
					params.retail_shop_id = this.form.retail_shop_id
				}
				const keyword = String(this.form.keyword || '').trim()
				if (keyword) {
					params.keyword = keyword
				}
				if (this.form.sync_rule) {
					params.sync_rule = this.form.sync_rule
				}
				if (this.form.stock_sync !== '') {
					params.stock_sync = Number(this.form.stock_sync)
				}
				const deleted = this.form.is_deleted === '' || this.form.is_deleted == null
					? 0
					: Number(this.form.is_deleted)
				params.is_deleted = deleted
				const goodsStatus = this.form.goods_status
				if (deleted !== 1 && goodsStatus !== '' && goodsStatus != null) {
					params.goods_status = Number(goodsStatus)
				}
				if (this.form.stock_sync_status !== '' && this.form.stock_sync_status != null) {
					params.stock_sync_status = Number(this.form.stock_sync_status)
				}
				if (this.form.price_sync_status !== '' && this.form.price_sync_status != null) {
					params.price_sync_status = Number(this.form.price_sync_status)
				}
				return params
			},
			fetchList() {
				this.loading = true
				this.listError = ''
				this.$axios.get(TB_GOOD_API.list, { params: this.buildListParams() }).then((res) => {
					if (res.data.code == 0) {
						const data = res.data.data || {}
						this.list = (data.list || []).map(this.normalizeRow)
						this.total = Number(data.total) || 0
						if (data.page) {
							this.page = Number(data.page) || this.page
						}
					} else {
						this.list = []
						this.total = 0
						this.listError = getApiErrorMessage(res, '获取淘宝商品失败')
					}
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.total = 0
					this.listError = getRequestErrorMessage(err, '获取淘宝商品失败，请稍后重试')
				}).finally(() => {
					this.loading = false
					this.calcTableHeight()
				})
			},
			syncShopQuery(shopId) {
				const query = Object.assign({}, this.$route.query)
				if (shopId) {
					query.retail_shop_id = String(shopId)
				} else {
					delete query.retail_shop_id
				}
				this.$router.replace({ path: this.$route.path, query: query }).catch(() => {})
			},
			onShopChange(val) {
				this.page = 1
				this.syncShopQuery(val)
				this.fetchList()
			},
			onSearch() {
				this.page = 1
				this.fetchList()
			},
			onReset() {
				const shopId = this.form.retail_shop_id
				this.form = this.emptyForm()
				this.form.retail_shop_id = shopId
				this.page = 1
				this.fetchList()
			},
			handleCurrentChange(page) {
				this.page = page
				this.fetchList()
			},
			handleSizeChange(size) {
				this.pageSize = size > 50 ? 50 : size
				this.page = 1
				this.fetchList()
			},
			handleSelectionChange(val) {
				this.selectedRows = val || []
			},
			getSelectedProductIds() {
				return this.selectedRows.map((row) => row.id).filter(Boolean).map(Number)
			},
			requireShop() {
				if (!this.form.retail_shop_id) {
					this.$message.warning('请先选择店铺')
					return false
				}
				return true
			},
			groupSelectedRowsByShop(rows) {
				const groups = []
				const map = {}
				let skippedNoShop = 0
				;(rows || []).forEach((row) => {
					const shopId = Number(row && row.retail_shop_id)
					if (!shopId) {
						skippedNoShop += 1
						return
					}
					const key = String(shopId)
					if (!map[key]) {
						map[key] = { shopId: shopId, rows: [] }
						groups.push(map[key])
					}
					map[key].rows.push(row)
				})
				return { groups: groups, skippedNoShop: skippedNoShop }
			},
			requireSelection() {
				if (!this.selectedRows.length) {
					this.$message.warning('请先选择商品')
					return false
				}
				return true
			},
			onGoodsStatusChange(value) {
				if (value !== '' && value != null && Number(this.form.is_deleted) === 1) {
					this.form.is_deleted = 0
				}
			},
			onDeletedChange(value) {
				if (Number(value) === 1) {
					this.form.goods_status = ''
				}
			},
			onBulkAction(action) {
				if (action.key === 'download') {
					this.onDownloadProducts()
					return
				}
				if (action.key === 'update_info') {
					this.onBatchUpdateInfo()
					return
				}
				if (action.key === 'batch_onsale') {
					this.onBatchSetSaleStatus(1)
					return
				}
				if (action.key === 'batch_offsale') {
					this.onBatchSetSaleStatus(0)
					return
				}
				if (!this.requireSelection()) {
					return
				}
				if (action.key === 'fixed_price_channel_shop' || action.key === 'safety_stock_price_shop') {
					this.onBatchPushStrategy(action.key)
					return
				}
				if (action.key === 'restore') {
					this.onRestore()
				}
			},
			onBatchSetSaleStatus(isOnsale) {
				if (!this.requireSelection()) {
					return
				}
				const grouped = this.groupSelectedRowsByShop(this.selectedRows)
				if (grouped.skippedNoShop) {
					this.$message.warning('已跳过 ' + grouped.skippedNoShop + ' 条缺少店铺的商品')
				}
				const payloads = []
				let skippedDeleted = 0
				grouped.groups.forEach((group) => {
					const liveRows = group.rows.filter((row) => !isTbGoodDeleted(row))
					skippedDeleted += group.rows.length - liveRows.length
					const goodIds = liveRows.map((row) => Number(row.id)).filter((id) => id > 0)
					if (goodIds.length) {
						payloads.push({ shopId: group.shopId, goodIds: goodIds })
					}
				})
				if (skippedDeleted) {
					this.$message.warning('已跳过 ' + skippedDeleted + ' 条已删除商品')
				}
				if (!payloads.length) {
					this.$message.warning('没有可操作的商品')
					return
				}
				this.saleStatusSubmitting = true
				const requests = payloads.map((item) => this.$axios.post(TB_GOOD_API.batchSetSaleStatus, {
					retail_shop_id: item.shopId,
					good_ids: item.goodIds,
					is_onsale: isOnsale,
				}, {
					timeout: TB_GOOD_SALE_STATUS_TIMEOUT,
				}))
				Promise.all(requests).then((results) => {
					let failed = 0
					let success = 0
					let failLogs = []
					let requestError = ''
					results.forEach((res) => {
						if (res.data.code != 0) {
							requestError = getApiErrorMessage(res, '批量操作失败')
							return
						}
						const data = res.data.data || {}
						failed += Number(data.failed) || 0
						success += Number(data.success) || 0
						failLogs = failLogs.concat((data.logs || []).filter((item) => item && !item.success))
					})
					if (requestError && success === 0 && failed === 0) {
						this.$message.error(requestError)
						return
					}
					if (failed > 0) {
						this.saleFailTitle = isOnsale === 1 ? '批量上架结果' : '批量下架结果'
						this.saleFailSummary = '成功 ' + success + ' 条，失败 ' + failed + ' 条'
						this.saleFailLogs = failLogs
						this.saleFailVisible = true
					} else if (isOnsale === 1) {
						this.$message.success('已提交上架，淘宝审核生效后状态会自动更新')
					} else {
						this.$message.success('下架成功')
					}
					this.fetchList()
				}).catch((err) => {
					console.log(err)
					if (isRequestTimeout(err)) {
						this.$message.warning('操作时间较长，请稍后刷新列表查看结果')
						this.fetchList()
						return
					}
					this.$message.error(getRequestErrorMessage(err, '批量操作失败'))
				}).finally(() => {
					this.saleStatusSubmitting = false
				})
			},
			onDownloadProducts() {
				if (!this.requireShop()) {
					return
				}
				this.$confirm('将拉取该店铺全部商品，不支持按勾选下载。单次最多 200 条，未拉完请再点一次。', '下载商品', {
					type: 'info',
					confirmButtonText: '开始拉取',
					cancelButtonText: '取消',
				}).then(() => {
					return this.$axios.post(TB_GOOD_API.sync, {
						retail_shop_id: Number(this.form.retail_shop_id),
					}, {
						timeout: TB_GOOD_SYNC_TIMEOUT,
					})
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success(formatTbGoodSyncStats(res.data.data))
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '下载商品失败'))
					}
				}).catch((err) => {
					if (err === 'cancel' || err === 'close') {
						return
					}
					console.log(err)
					if (isRequestTimeout(err)) {
						this.$message.warning('同步时间较长，后台仍在处理，请稍后刷新列表')
						this.fetchList()
						return
					}
					this.$message.error(getRequestErrorMessage(err, '下载商品失败，请稍后重试'))
				})
			},
			onBatchUpdateInfo() {
				const selected = this.selectedRows || []
				if (selected.length) {
					const grouped = this.groupSelectedRowsByShop(selected)
					if (grouped.skippedNoShop) {
						this.$message.warning('已跳过 ' + grouped.skippedNoShop + ' 条缺少店铺的商品')
					}
					if (!grouped.groups.length) {
						this.$message.warning('勾选商品没有所属店铺')
						return
					}
					const requests = grouped.groups.map((group) => {
						const ids = group.rows.map((row) => Number(row.id)).filter((id) => id > 0)
						return this.$axios.post(TB_GOOD_API.syncTrigger, {
							retail_shop_id: group.shopId,
							good_ids: ids,
							product_ids: ids,
						})
					})
					Promise.all(requests).then((results) => {
						let totalTasks = 0
						let requestError = ''
						results.forEach((res) => {
							if (res.data.code != 0) {
								requestError = getApiErrorMessage(res, '触发同步失败')
								return
							}
							const tasks = (res.data.data && res.data.data.tasks) || []
							totalTasks += tasks.length
						})
						if (requestError && totalTasks === 0) {
							this.$message.error(requestError)
							return
						}
						const shopNote = grouped.groups.length > 1 ? '（' + grouped.groups.length + ' 个店铺）' : ''
						const msg = totalTasks
							? '已为 ' + totalTasks + ' 个商品触发同步' + shopNote + '，可在 Sku更新记录 查看结果'
							: '已触发同步'
						this.$message.success(msg)
						this.fetchList()
					}).catch((err) => {
						console.log(err)
						this.$message.error(getRequestErrorMessage(err, '触发同步失败'))
					})
					return
				}
				if (!this.requireShop()) {
					return
				}
				this.$confirm('未勾选商品，将同步该店铺全部商品。是否继续？', '批量更新信息', {
					type: 'warning',
					confirmButtonText: '同步全店',
					cancelButtonText: '取消',
				}).then(() => {
					return this.$axios.post(TB_GOOD_API.syncTrigger, {
						retail_shop_id: Number(this.form.retail_shop_id),
						good_ids: [],
						product_ids: [],
					})
				}).then((res) => {
					if (res.data.code == 0) {
						const tasks = (res.data.data && res.data.data.tasks) || []
						const msg = tasks.length
							? '已为 ' + tasks.length + ' 个商品触发同步，可在 Sku更新记录 查看结果'
							: (res.data.message || res.data.msg || '已触发同步')
						this.$message.success(msg)
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '触发同步失败'))
					}
				}).catch((err) => {
					if (err === 'cancel' || err === 'close') {
						return
					}
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '触发同步失败'))
				})
			},
			onBatchPushStrategy(pushStrategy) {
				const productIds = this.getSelectedProductIds()
				this.$axios.post(TB_GOOD_API.pushStrategySave, {
					id: productIds,
					push_strategy: pushStrategy,
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('同步规则已更新')
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '更新失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '更新失败'))
				})
			},
			getStockSyncValue(row) {
				if (!row || row.stock_sync == null || row.stock_sync === '') {
					return 0
				}
				return Number(row.stock_sync) === 1 ? 1 : 0
			},
			onStockSyncChange(row, value) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				if (isTbGoodDeleted(row)) {
					this.$message.warning('商品已删除，无法修改')
					return
				}
				const prev = this.getStockSyncValue(row)
				const nextValue = Number(value) === 1 ? 1 : 0
				row.stock_sync = nextValue
				this.$axios.post(TB_GOOD_API.productUpdate, {
					id: Number(row.id),
					is_sync: nextValue,
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('同步库存状态已更新')
					} else {
						this.$message.error(getApiErrorMessage(res, '更新失败'))
						row.stock_sync = prev
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '更新失败'))
					row.stock_sync = prev
				})
			},
			onSyncRuleChange(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					this.fetchList()
					return
				}
				this.$axios.post(TB_GOOD_API.pushStrategySave, {
					id: row.id,
					push_strategy: row.sync_rule,
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('同步规则已更新')
					} else {
						this.$message.error(getApiErrorMessage(res, '更新失败'))
						this.fetchList()
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '更新失败'))
					this.fetchList()
				})
			},
			showRuleConfigBtn(row) {
				return !!(row && row.sync_rule && row.sync_rule !== 'manual')
			},
			resolveShopId(row) {
				if (row && row.retail_shop_id) {
					return String(row.retail_shop_id)
				}
				if (this.form.retail_shop_id) {
					return String(this.form.retail_shop_id)
				}
				return ''
			},
			jumpToShopRule(row) {
				const shopId = this.resolveShopId(row)
				const path = resolveTbShopRulePath(row.sync_rule)
				if (!shopId) {
					this.$message.warning('请先选择店铺')
					return
				}
				if (!path) {
					this.$message.warning('当前规则暂不支持跳转店铺配置')
					return
				}
				sessionStorage.setItem('retailShopId', shopId)
				sessionStorage.setItem('storeConfigPlatform', 'taobao')
				this.$router.push({
					path: path,
					query: { id: shopId },
				})
			},
			onRuleConfig(row) {
				if (!row || !row.id) {
					return
				}
				if (isTbShopPushStrategy(row.sync_rule)) {
					const isC3 = String(row.sync_rule).indexOf('safety_stock_price') === 0
					const message = isC3
						? '当前商品使用店铺安全库存通用规则，参数请在店铺配置中修改。是否前往？'
						: '当前商品使用店铺一口价通用规则，参数请在店铺配置中修改。是否前往？'
					this.$confirm(message, '参数配置', {
						confirmButtonText: '前往店铺配置',
						cancelButtonText: '取消',
						type: 'info',
					}).then(() => {
						this.jumpToShopRule(row)
					}).catch(() => {})
					return
				}
				if (isTbProductPushStrategy(row.sync_rule)) {
					this.openChannelConfig(row)
					return
				}
				this.configRow = row
				this.configType = resolvePushConfigType(row.sync_rule)
				this.configTitle = '参数配置'
				this.configVisible = true
				this.$axios.get(TB_GOOD_API.pushConfigGet, {
					params: {
						id: Number(row.id),
						type: this.configType,
					},
				}).then((res) => {
					if (res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取配置失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '获取配置失败'))
				})
			},
			onSavePushConfig() {
				if (!this.configRow || !this.configRow.id) {
					return
				}
				this.configSaving = true
				this.$axios.post(TB_GOOD_API.pushConfigSave, {
					id: Number(this.configRow.id),
					type: this.configType,
					data: {},
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('配置已保存')
						this.configVisible = false
					} else {
						this.$message.error(getApiErrorMessage(res, '保存失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '保存失败'))
				}).finally(() => {
					this.configSaving = false
				})
			},
			onViewSku(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				this.skuTitle = (row.goods_name || row.title) ? ('SKU编码 - ' + (row.goods_name || row.title)) : 'SKU编码'
				this.skuVisible = true
				this.skuLoading = true
				this.skuSaving = false
				this.skuGeneratingAll = false
				this.skuList = []
				this.$axios.get(TB_GOOD_API.detail, { params: { id: row.id } }).then((res) => {
					if (res.data.code == 0) {
						const data = res.data.data || {}
						this.skuList = (data.skus || []).map((item) => {
							const next = Object.assign({}, item)
							next.sku_id = stringifySkuId(next.sku_id)
							next._savedOuterId = String(next.outer_id == null ? '' : next.outer_id).trim()
							return next
						})
					} else {
						this.$message.error(getApiErrorMessage(res, '获取SKU失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '获取SKU失败，请稍后重试'))
				}).finally(() => {
					this.skuLoading = false
				})
			},
			resolveSkuLocalId(row) {
				if (!row || row.id == null || row.id === '') {
					return null
				}
				return row.id
			},
			canEditSkuOuterId(row) {
				return this.resolveSkuLocalId(row) != null
			},
			skuOuterIdDraft(row) {
				return String((row && row.outer_id) || '').trim()
			},
			isSkuOuterIdDirty(row) {
				return this.skuOuterIdDraft(row) !== String((row && row._savedOuterId) || '').trim()
			},
			skuRowLabel(row, index) {
				const skuId = stringifySkuId(row && row.sku_id)
				if (skuId) {
					return skuId
				}
				const size = (row && (row.size_key || row.size)) || ''
				if (size) {
					return String(size)
				}
				return '第' + (index + 1) + '行'
			},
			onSkuOuterIdInput(row, value) {
				this.$set(row, 'outer_id', value)
				if (row._genPreview) {
					this.$set(row, '_genPreview', null)
				}
			},
			skuGeneratePreviewText(row) {
				const preview = row && row._genPreview
				if (!preview) {
					return ''
				}
				const style = preview.style_number || '-'
				const sizeKey = preview.size_key || '-'
				return '当前规则：' + formatSkuOuterIdRuleLabel(preview.rule) + '；将解析为 款号 ' + style + ' / 尺码 ' + sizeKey
			},
			skuColorWarningText(row) {
				return String((row && row._colorWarning) || '').trim()
			},
			applySkuColorSize(row, data) {
				if (!row || !data) {
					return
				}
				if (Object.prototype.hasOwnProperty.call(data, 'color')) {
					this.$set(row, 'color', data.color)
				}
				if (Object.prototype.hasOwnProperty.call(data, 'size')) {
					this.$set(row, 'size', data.size)
				}
			},
			generateOneSkuOuterId(row) {
				const skuId = this.resolveSkuLocalId(row)
				if (skuId == null) {
					return Promise.resolve({ ok: false, empty: false })
				}
				this.$set(row, '_generating', true)
				return this.$axios.get(TB_GOOD_API.skuOuterIdGenerate, { params: { sku_id: skuId } }).then((res) => {
					if (res.data.code != 0) {
						return { ok: false, empty: false, message: getApiErrorMessage(res, '生成编码失败') }
					}
					const data = res.data.data || {}
					const outerId = data.outer_id == null ? '' : String(data.outer_id)
					const colorWarning = String(data.color_warning || '').trim()
					this.applySkuColorSize(row, data)
					this.$set(row, '_colorWarning', colorWarning)
					if (!outerId.trim()) {
						this.$set(row, '_genPreview', null)
						return { ok: false, empty: true, colorWarning: colorWarning }
					}
					this.$set(row, 'outer_id', outerId)
					this.$set(row, '_genPreview', {
						rule: data.rule,
						style_number: data.style_number,
						size_key: data.size_key,
						color: data.color,
						size: data.size,
					})
					return { ok: true, empty: false, colorWarning: colorWarning }
				}).catch((err) => {
					console.log(err)
					return { ok: false, empty: false, message: getRequestErrorMessage(err, '生成编码失败') }
				}).finally(() => {
					this.$set(row, '_generating', false)
				})
			},
			onGenerateAllSkuOuterIds() {
				const editable = (this.skuList || []).filter((row) => this.canEditSkuOuterId(row))
				if (!editable.length) {
					this.$message.warning('没有可生成的 SKU')
					return
				}
				const emptyRows = editable.filter((row) => !this.skuOuterIdDraft(row))
				if (emptyRows.length) {
					this.runGenerateSkuOuterIds(emptyRows, {
						skippedFilled: editable.length - emptyRows.length,
					})
					return
				}
				this.$confirm(
					'当前 SKU 均已有编码。确定按店铺规则重新生成？已有编码将被覆盖，需再点保存才会同步到淘宝。',
					'重新生成编码',
					{
						type: 'warning',
						confirmButtonText: '覆盖并重新生成',
						cancelButtonText: '取消',
					}
				).then(() => {
					this.runGenerateSkuOuterIds(editable, { overwrite: true })
				}).catch(() => {})
			},
			runGenerateSkuOuterIds(rows, options) {
				const opts = options || {}
				this.skuGeneratingAll = true
				const stats = { filled: 0, empty: 0, failed: 0 }
				const colorWarnings = []
				const run = (index) => {
					if (index >= rows.length) {
						this.skuGeneratingAll = false
						this.reportSkuGenerateSummary(stats, opts, colorWarnings)
						return
					}
					this.generateOneSkuOuterId(rows[index]).then((result) => {
						if (result && result.colorWarning && colorWarnings.indexOf(result.colorWarning) === -1) {
							colorWarnings.push(result.colorWarning)
						}
						if (result && result.ok) {
							stats.filled += 1
						} else if (result && result.empty) {
							stats.empty += 1
						} else {
							stats.failed += 1
						}
						run(index + 1)
					})
				}
				run(0)
			},
			reportSkuGenerateSummary(stats, options, colorWarnings) {
				const parts = []
				if (stats.filled) {
					parts.push(stats.filled + ' 条已预填')
				}
				if (stats.empty) {
					parts.push(stats.empty + ' 条无法自动生成')
				}
				if (stats.failed) {
					parts.push(stats.failed + ' 条生成失败')
				}
				if (options.skippedFilled) {
					parts.push(options.skippedFilled + ' 条已有编码未改')
				}
				if (!parts.length) {
					this.$message.warning('无法自动生成编码，请先核对商品款号和尺码数据')
					this.notifySkuColorWarnings(colorWarnings)
					return
				}
				const text = parts.join('，')
				if (stats.failed || (stats.empty && !stats.filled)) {
					this.$message.warning(text)
					this.notifySkuColorWarnings(colorWarnings)
					return
				}
				if (stats.empty) {
					this.$message.warning(text + '，请核对商品数据后再保存')
					this.notifySkuColorWarnings(colorWarnings)
					return
				}
				this.$message.success(text + '，请确认后点击保存')
				this.notifySkuColorWarnings(colorWarnings)
			},
			notifySkuColorWarnings(colorWarnings) {
				const unique = []
				;(colorWarnings || []).forEach((text) => {
					if (text && unique.indexOf(text) === -1) {
						unique.push(text)
					}
				})
				if (!unique.length) {
					return
				}
				this.$message.warning({
					message: unique.join('；'),
					duration: 6000,
				})
			},
			applySkuOuterIdSaveResult(row, data) {
				const next = data || {}
				if (next.outer_id != null) {
					this.$set(row, 'outer_id', next.outer_id)
				}
				if (Object.prototype.hasOwnProperty.call(next, 'style_number')) {
					this.$set(row, 'style_number', next.style_number)
				}
				if (Object.prototype.hasOwnProperty.call(next, 'size_key')) {
					this.$set(row, 'size_key', next.size_key)
				} else if (next.matched === false || next.matched === 0) {
					this.$set(row, 'size_key', '')
				}
				this.applySkuColorSize(row, next)
				this.$set(row, '_savedOuterId', this.skuOuterIdDraft(row))
				this.$set(row, '_genPreview', null)
			},
			saveOneSkuOuterId(row) {
				const skuId = this.resolveSkuLocalId(row)
				const outerId = this.skuOuterIdDraft(row)
				return this.$axios.post(TB_GOOD_API.skuOuterIdSave, {
					sku_id: skuId,
					outer_id: outerId,
				}).then((res) => {
					if (res.data.code != 0) {
						return {
							ok: false,
							message: this.skuRowLabel(row) + '：' + getApiErrorMessage(res, '保存失败'),
						}
					}
					const data = res.data.data || {}
					this.applySkuOuterIdSaveResult(row, data)
					return {
						ok: true,
						matched: !(data.matched === false || data.matched === 0),
					}
				}).catch((err) => {
					console.log(err)
					return {
						ok: false,
						message: this.skuRowLabel(row) + '：' + getRequestErrorMessage(err, '保存失败'),
					}
				})
			},
			onSaveAllSkuOuterIds() {
				const list = this.skuList || []
				const seen = {}
				const dupes = []
				const emptyDirty = []
				const missingId = []
				const toSave = []
				list.forEach((row, index) => {
					const draft = this.skuOuterIdDraft(row)
					const label = this.skuRowLabel(row, index)
					if (draft) {
						if (seen[draft]) {
							dupes.push(draft)
						} else {
							seen[draft] = true
						}
					}
					if (!this.isSkuOuterIdDirty(row)) {
						return
					}
					if (!this.canEditSkuOuterId(row)) {
						missingId.push(label)
						return
					}
					if (!draft) {
						emptyDirty.push(label)
						return
					}
					toSave.push(row)
				})
				if (dupes.length) {
					const uniqueDupes = []
					dupes.forEach((code) => {
						if (uniqueDupes.indexOf(code) === -1) {
							uniqueDupes.push(code)
						}
					})
					this.$message.warning('同一商品内编码不能重复：' + uniqueDupes.join('、'))
					return
				}
				if (missingId.length) {
					this.$message.warning('以下 SKU 缺少本地主键，无法保存：' + missingId.join('、'))
					return
				}
				if (!toSave.length) {
					if (emptyDirty.length) {
						this.$message.warning('编码为空的行未保存，请先填写或生成编码')
					} else {
						this.$message.info('没有需要保存的编码变更')
					}
					return
				}
				this.skuSaving = true
				const matched = []
				const unmatched = []
				const failed = []
				const run = (index) => {
					if (index >= toSave.length) {
						this.skuSaving = false
						this.reportSkuSaveSummary({
							matchedCount: matched.length,
							unmatchedCount: unmatched.length,
							failed: failed,
							skippedEmpty: emptyDirty,
						})
						return
					}
					this.saveOneSkuOuterId(toSave[index]).then((result) => {
						if (result && result.ok) {
							if (result.matched) {
								matched.push(toSave[index])
							} else {
								unmatched.push(toSave[index])
							}
						} else {
							failed.push((result && result.message) || (this.skuRowLabel(toSave[index]) + '：保存失败'))
						}
						run(index + 1)
					})
				}
				run(0)
			},
			reportSkuSaveSummary(summary) {
				const parts = []
				if (summary.matchedCount) {
					parts.push(summary.matchedCount + ' 条已保存并可匹配库存')
				}
				if (summary.unmatchedCount) {
					parts.push(summary.unmatchedCount + ' 条已保存但无法匹配库存')
				}
				if (summary.skippedEmpty && summary.skippedEmpty.length) {
					parts.push(summary.skippedEmpty.length + ' 条编码为空已跳过')
				}
				if (summary.failed && summary.failed.length) {
					parts.push(summary.failed.length + ' 条保存失败')
				}
				let text = parts.join('，')
				if (summary.failed && summary.failed.length) {
					text += '。' + summary.failed.slice(0, 2).join('；')
				}
				if (summary.failed && summary.failed.length && !summary.matchedCount && !summary.unmatchedCount) {
					this.$message.error(text)
					return
				}
				if ((summary.unmatchedCount || (summary.failed && summary.failed.length) || (summary.skippedEmpty && summary.skippedEmpty.length)) && parts.length) {
					this.$message.warning(text)
					return
				}
				this.$message.success(text || '编码已保存并同步到淘宝，可正常匹配库存')
			},
			onSkuClosed() {
				this.skuList = []
				this.skuTitle = 'SKU编码'
				this.skuSaving = false
				this.skuGeneratingAll = false
			},
			onSkuLog(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				this.skuLogRow = row
				this.pageView = 'skuLog'
			},
			closeSkuLog() {
				this.pageView = 'list'
				this.skuLogRow = null
				this.$nextTick(this.calcTableHeight)
			},
			onChannelConfig(row) {
				this.openChannelConfig(row)
			},
			openChannelConfig(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				this.channelRow = row
				this.pageView = 'channelConfig'
			},
			closeChannelConfig() {
				this.pageView = 'list'
				this.channelRow = null
				this.fetchList()
				this.$nextTick(this.calcTableHeight)
			},
			onChannelRuleChange(syncRule) {
				if (this.channelRow) {
					this.channelRow.sync_rule = syncRule
				}
			},
			onDeleteRow(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				const physical = isTbGoodDeleted(row)
				const message = physical
					? '确定彻底删除该商品？此操作不可恢复。'
					: '确定将该商品移入回收站？'
				this.$confirm(message, '提示', {
					type: physical ? 'warning' : 'info',
					confirmButtonText: physical ? '彻底删除' : '移入回收站',
					cancelButtonText: '取消',
				}).then(() => {
					return this.$axios.post(TB_GOOD_API.delete, {
						retail_shop_product_id: Number(row.id),
					})
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success(physical ? '已彻底删除' : '已移入回收站')
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '删除失败'))
					}
				}).catch((err) => {
					if (err === 'cancel') {
						return
					}
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '删除失败'))
				})
			},
			onRestoreRow(row) {
				if (!row || !row.id) {
					this.$message.warning('商品信息不完整')
					return
				}
				this.$axios.post(TB_GOOD_API.restore, {
					retail_shop_product_ids: [Number(row.id)],
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('恢复成功')
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '恢复失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '恢复失败'))
				})
			},
			onRestore() {
				this.$axios.post(TB_GOOD_API.restore, {
					retail_shop_product_ids: this.getSelectedProductIds(),
				}).then((res) => {
					if (res.data.code == 0) {
						this.$message.success('恢复成功')
						this.fetchList()
					} else {
						this.$message.error(getApiErrorMessage(res, '恢复失败'))
					}
				}).catch((err) => {
					console.log(err)
					this.$message.error(getRequestErrorMessage(err, '恢复失败'))
				})
			},
		},
	}
</script>

<style scoped lang="less">
	@import '../storeTable.less';

	.tb-goods-page {
		height: 100%;
		min-height: calc(100vh - 62px);
		padding: 8px 12px 0;
		box-sizing: border-box;
		background: linear-gradient(180deg, #f7f9fc 0%, #f3f5f8 100%);
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.rule-config-panel {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background: #fff;
		border-radius: 10px;
		box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
		overflow: hidden;
	}

	.rule-config-head {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 10px 14px;
		border-bottom: 1px solid #eef0f3;
	}

	.rule-config-goods {
		font-size: 13px;
		color: #606266;
	}

	.rule-config-body {
		flex: 1;
		min-height: 0;
		overflow: auto;
	}

	.filter-card,
	.toolbar-card,
	.table-card,
	.pager-card {
		background: #fff;
		border-radius: 10px;
		box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
	}

	.filter-card {
		padding: 10px 14px 8px;
		flex-shrink: 0;
	}

	.filter-row {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		gap: 8px;
		width: 100%;

		&--second {
			margin-top: 6px;
		}
	}

	.filter-actions {
		display: flex;
		align-items: center;
		flex: 0 0 152px;
		justify-content: flex-end;
		gap: 6px;
	}

	.btn-search {
		background: linear-gradient(135deg, #ff6b3d 0%, #ff4714 100%) !important;
		border: none !important;
	}

	.btn-ghost {
		color: #606266;
		border-color: #e4e7ed;
		background: #fafbfc;
	}

	.filter-form {
		display: block;
		width: 100%;

		.filter-row > .el-form-item {
			flex: 1;
			min-width: 0;
			margin-bottom: 0;
			margin-right: 0;
		}

		.filter-control,
		.keyword-input,
		.labeled-select {
			width: 100%;
		}

		.keyword-item /deep/ .el-form-item__content {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		.keyword-label {
			flex-shrink: 0;
			font-size: 12px;
			color: #606266;
			white-space: nowrap;
		}
	}

	.toolbar-card {
		padding: 10px 14px;
		flex-shrink: 0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 12px 20px;
	}

	.toolbar-btns {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.tool-btn {
		border: none;
		outline: none;
		padding: 5px 12px;
		font-size: 12px;
		line-height: 1.4;
		border-radius: 6px;
		cursor: pointer;
		color: #fff;

		&--brand {
			background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
		}

		&:disabled {
			opacity: 0.55;
			cursor: not-allowed;
		}
	}

	.selection-tip {
		margin-left: auto;
		font-size: 12px;
		color: #606266;
		padding: 4px 10px;
		border-radius: 999px;
		background: rgba(255, 71, 20, 0.08);

		strong {
			color: #ff4714;
		}
	}

	.table-card {
		flex: 1;
		min-height: 0;
		overflow: hidden;
	}

	.goods-table {
		.store-table-border();
		font-size: 13px;

		/deep/ th.el-table__cell {
			background: #fafbfc;
			font-weight: normal;
			color: #606266;
		}
	}

	.thumb {
		width: 48px;
		height: 48px;
		margin: 0 auto;
		border-radius: 8px;
		overflow: hidden;
		background: #f4f6f8;
		display: flex;
		align-items: center;
		justify-content: center;

		/deep/ .el-image {
			width: 100%;
			height: 100%;
		}

		i {
			font-size: 20px;
			color: #c0c4cc;
		}
	}

	.cell-main {
		color: #303133;
	}

	.cell-sub,
	.cell-time {
		font-size: 12px;
		color: #909399;
	}

	.status-tag {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 999px;
		font-size: 12px;

		&.is-on {
			color: #059669;
			background: rgba(5, 150, 105, 0.1);
		}

		&.is-off {
			color: #6b7280;
			background: #f3f4f6;
		}

		&.is-soldout {
			color: #d97706;
			background: rgba(217, 119, 6, 0.12);
		}

		&.is-deleted {
			color: #dc2626;
			background: rgba(220, 38, 38, 0.1);
		}
	}

	.sync-status-tag {
		display: inline-block;
		padding: 0 8px;
		height: 22px;
		line-height: 22px;
		font-size: 12px;
		border-radius: 4px;
		border: 1px solid transparent;
	}

	.sync-status-tag--success {
		color: #67c23a;
		background: #f0f9eb;
		border-color: #c2e7b0;
	}

	.sync-status-tag--danger {
		color: #f56c6c;
		background: #fef0f0;
		border-color: #fbc4c4;
	}

	.sync-status-tag--warning {
		color: #e6a23c;
		background: #fdf6ec;
		border-color: #f5dab1;
	}

	.sync-status-tag--info {
		color: #909399;
		background: #f4f4f5;
		border-color: #d3d4d6;
	}

	.status-cell {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 4px 8px;
		border-radius: 20px;

		.status-label {
			font-size: 12px;
			white-space: nowrap;
		}

		&.is-on {
			background: rgba(255, 71, 20, 0.1);

			.status-label {
				color: #ff4714;
			}
		}

		&.is-off {
			background: #f5f6f8;

			.status-label {
				color: #909399;
			}
		}
	}

	.rule-select {
		width: 130px;
		margin-bottom: 4px;
	}

	.link-btn {
		border: none;
		background: none;
		padding: 0;
		font-size: 13px;
		color: #2563eb;
		cursor: pointer;
	}

	.row-actions {
		display: grid;
		grid-template-columns: 1fr 1fr;
		justify-items: stretch;
		gap: 6px 8px;
	}

	.action-chip {
		border: 1px solid rgba(255, 71, 20, 0.25);
		background: rgba(255, 71, 20, 0.06);
		color: #ff4714;
		padding: 4px 6px;
		font-size: 12px;
		line-height: 1.3;
		border-radius: 999px;
		cursor: pointer;
		white-space: nowrap;
	}

	.pager-card {
		flex-shrink: 0;
		padding: 6px 12px 8px;
		display: flex;
		justify-content: flex-end;
	}

	.config-tip {
		margin: 0 0 12px;
		font-size: 13px;
		line-height: 1.6;
		color: #606266;
	}

	.sale-fail-summary {
		margin-bottom: 12px;
		font-size: 13px;
		color: #606266;
	}

	.sku-outer-id-cell {
		text-align: left;
	}

	.sku-outer-id-hint {
		margin: 6px 0 0;
		font-size: 12px;
		line-height: 1.4;
		color: #909399;

		&--warn {
			color: #e6a23c;
		}
	}

	.size-key-cell {
		line-height: 1.4;
	}

	.size-key-empty {
		display: inline-block;
		font-size: 12px;
		line-height: 1.4;
		color: #f56c6c;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/taobao/goods.vue